import * as React from 'react';
import { connect } from 'react-redux';
import { actionCreators, IJiraConnectionInfo, IConnectionRefreshInfo } from "../../../store/integration/JiraStore";
import { ApplicationState } from "../../../store/index";
import {
    Dialog, DialogType, TextField, DialogFooter, PrimaryButton, DefaultButton,
    Spinner, SpinnerSize, MessageBar, MessageBarType, Dropdown
} from 'office-ui-fabric-react';
import { SourceType } from '../../../store/ExternalEpmConnectStore';
import { ConnectionTitle } from '../ConnectionTitle';
import ConnectionEditWrapper from '../ConnectionEditWrapper';
import { useDidMountEffect, useWillMountEffect } from '../../utils/effects';
import { OAuth20AuthControl } from './OAuth20AuthControl';
import { BasicAuthControl } from './BasicAuthControl';
import { OAuth1AuthControl } from './OAuth1AuthControl';
import { Validator } from '../../../validation';

export const oauthTarget = "connectionDataReceived_jira";

export const validation = {
    url: Validator.new().required().build(),
    login: Validator.new().required().build(),
    apiToken: Validator.new().required().build(),

    oAuthToken: Validator.new().required().build(),
    oAuthSessionHandle: Validator.new().required().build(),
}

type OwnProps = {
    onDismiss: () => void,
    onCreated?: (connectionId: string) => void;
    connectionId?: string;
    isRestricted?: boolean
}

type StateProps = {
    isProcessing: boolean; error: string | null,
    isRefreshingInfo?: boolean;
    refreshInfo: IConnectionRefreshInfo | null,
    createdConnectionId?: string
}

type Props = OwnProps & typeof actionCreators & StateProps;

enum AuthenticationType {
    Basic = 0,
    OAuth1 = 1,
    OAuth2 = 2,
}

const EmptyBasic: Partial<IJiraConnectionInfo> = {
    login: undefined,
    apiToken: undefined
}

const EmptyOAuth: Partial<IJiraConnectionInfo> = {
    oAuthToken: undefined,
    oAuthTokenSecret: undefined,
    oAuthSessionHandle: undefined,
    oAuthExpiresIn: undefined,
    oAuthAuthorizationExpiresIn: undefined
}

const EmptyOAuth20: Partial<IJiraConnectionInfo> = {
    oAuth20Token: undefined,
    oAuth20RefreshToken: undefined,
    oAuth20ClientId: undefined,
    oAuth20ClientSecret: undefined,
    oAuth20ExpiresIn: undefined,
    oAuth20CreatedAt: undefined
}

type AuthProps = {
    isEdit: boolean;
    connection: IJiraConnectionInfo;
    onChange: (connection: Partial<IJiraConnectionInfo>, isValid: boolean, error?: string) => void
}

const isBasicValid = (info: { login?: string, apiToken?: string }) => {
    return validation.login.isValid(info.login) && validation.apiToken.isValid(info.apiToken);
}

const isOAuthValid = (info: { oAuthToken?: string, oAuthSessionHandle?: string }) => {
    return validation.oAuthToken.isValid(info.oAuthToken)
        && validation.oAuthToken.isValid(info.oAuthSessionHandle);
}

const getAuthType = (connection?: IJiraConnectionInfo): AuthenticationType | string =>
    connection?.id
        ? connection.login
            ? AuthenticationType.Basic
            : connection.oAuth20ClientId
                ? AuthenticationType.OAuth2
                : AuthenticationType.OAuth1
        : '';

const AuthSelector = (props: AuthProps) => {
    const [authType, setAuthType] = React.useState<AuthenticationType | string>(getAuthType(props.connection));

    React.useEffect(() => {
        props.connection.id && setAuthType(getAuthType(props.connection));
    }, [props.connection]);

    const { isEdit, connection, onChange } = props;
    return <>
        <TextField
            autoFocus={true}
            disabled={isEdit}
            validateOnFocusOut={true}
            validateOnLoad={false}
            value={connection.url}
            label="Jira URL"
            placeholder="your_domain.atlassian.net"
            onChange={(e, _) => onChange({ url: _?.trim() }, isBasicValid(connection))}
            onGetErrorMessage={validation.url.getErrorMessage} />
        <Dropdown
            label="Authentication Type"
            disabled={isEdit || !connection.url}
            selectedKey={authType}
            options={[
                { key: '', text: 'Select authentication' },
                { key: AuthenticationType.Basic, text: 'Basic (login/password)' },
                { key: AuthenticationType.OAuth1, text: 'OAuth 1.0 (Jira Server only)' },
                { key: AuthenticationType.OAuth2, text: 'OAuth 2.0 (Jira Server only)' },
            ]}
            onChange={(e, o) => {
                if (!o) return;
                setAuthType(o.key);

                if (o.key === AuthenticationType.Basic) {
                    onChange({ ...connection, ...EmptyOAuth, ...EmptyOAuth20 }, false);
                } else if (o.key === AuthenticationType.OAuth1) {
                    onChange({ ...connection, ...EmptyBasic, ...EmptyOAuth20 }, false);
                } else if (o.key === AuthenticationType.OAuth2) {
                    onChange({ ...connection, ...EmptyBasic, ...EmptyOAuth }, false);
                }
        }} />
        <div>
            {authType === AuthenticationType.Basic && <BasicAuthControl
                login={connection.login}
                apiToken={connection.apiToken}
                onChange={_ => onChange(_, isBasicValid(_))}
            />}
            {authType === AuthenticationType.OAuth1 && <OAuth1AuthControl
                url={connection.url}
                setError={_ => onChange(connection, false, _)}
                onSuccess={_ => onChange(_, isOAuthValid(_))}
            />}
            {authType === AuthenticationType.OAuth2 && <OAuth20AuthControl
                url={connection.url}
                clientId={connection.oAuth20ClientId}
                clientSecret={connection.oAuth20ClientSecret}
                setError={_ => onChange(connection, false, _)}
                onSuccess={_ => onChange(_, true)}
            />}
        </div>
    </>
}

const JiraConnectionEdit = (props: Props) => {
    const [connection, setConnection] = React.useState<IJiraConnectionInfo>({
        url: "",
        login: '',
        apiToken: '',
        id: props.connectionId,
        isRestricted: props.isRestricted ?? false
    });
    const [isInited, setIsInited] = React.useState(!props.connectionId);
    const [isRefreshing, setIsRefreshing] = React.useState(true);
    const [isValid, setIsValid] = React.useState(false);
    const [authError, setAuthError] = React.useState<string | undefined | null>();

    const dismiss = () => {
        props.onDismiss();
        props.cleanError();
    }

    useWillMountEffect(() => {
        if (props.connectionId) {
            props.loadRefreshInfo(props.connectionId);
        } else {
            setIsRefreshing(false);
        }
    });

    React.useEffect(() => {
        setIsRefreshing(!!props.isRefreshingInfo);
    }, [props.isRefreshingInfo]);

    React.useEffect(() => {
        if (!props.connectionId || !props.refreshInfo || isRefreshing) {
            return;
        }

        const info = props.refreshInfo;
        setConnection({
            ...connection,
            id: props.connectionId,
            url: info.url,
            login: info.login,
            oAuth20ClientId: info.oAuth20ClientId,
            oAuth20ClientSecret: info.oAuth20ClientSecret,
        });
        setIsInited(true);
    }, [props.refreshInfo, isRefreshing]);

    useDidMountEffect(() => {
        if (props.isProcessing || props.error) {
            return;
        }

        props.onCreated && props.createdConnectionId && props.onCreated(props.createdConnectionId);
        dismiss();
    }, [props.isProcessing]);

    const isEdit = !!connection.id;
    const okText = isEdit ? "Save" : "Create";
    const error = authError ?? props.error;
    return <Dialog
        hidden={false}
        minWidth={400}
        maxWidth={400}
        dialogContentProps={{
            onDismiss: dismiss,
            type: DialogType.normal,
            title: <ConnectionTitle isEdit={isEdit} sourceType={SourceType.Jira} />,
            className: "jira-connect"
        }}
        modalProps={{
            isBlocking: true,
            containerClassName: 'ms-dialogMainOverride connection-popup'
        }}>
        <ConnectionEditWrapper sourceType={SourceType.Jira}
            articleGuidePath="https://help.ppm.express/89435-jira-connection/522458"
            videoGuidePath="https://help.ppm.express/89435-jira-connection/1275629">
            <div className="connection-wrap">
                <AuthSelector
                    key={isInited ? 'selector' : ''}
                    connection={connection}
                    isEdit={isEdit}
                    onChange={(c, isValid, error) => {
                        setConnection({ ...connection, ...c });
                        setIsValid(isValid);
                        setAuthError(error);
                    }}
                />
                {error && <p>
                    <MessageBar
                        className='margin-top'
                        messageBarType={MessageBarType.severeWarning}
                        isMultiline={true}>
                        {error}
                    </MessageBar>
                </p>}
            </div>
        </ConnectionEditWrapper>
        <DialogFooter>
            {
                !props.isProcessing && [<PrimaryButton
                    key="ok"
                    disabled={isRefreshing || !isValid}
                    onClick={() => isValid && props.createOrRefreshConnection(connection)}
                    text={okText} />,
                <DefaultButton key="cancel" onClick={dismiss} text="Cancel" />]
            }
            {
                props.isProcessing && [<PrimaryButton key="processing-ok" disabled={true} text={okText}>
                    <Spinner size={SpinnerSize.medium} />
                </PrimaryButton>,
                <DefaultButton key="processing-cancel" disabled={true} text="Cancel" />
                ]
            }
        </DialogFooter>
    </Dialog>;
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        isProcessing: state.jira.connections.isProcessing,
        error: state.jira.connections.error,
        isRefreshingInfo: state.jira.connections.isRefreshingInfo,
        refreshInfo: state.jira.connections.refreshInfo,
        createdConnectionId: state.jira.connections.createdConnectionId
    };
}

export default connect(mapStateToProps, actionCreators)(JiraConnectionEdit);